import { Dispatch, SetStateAction } from 'react'
import { Observable, of } from 'rxjs'
import { catchError, map, mapTo, tap } from 'rxjs/operators'
import {
  APICommentPicture,
  APICommentPictureRequest,
  APICommentPictureWithMakerUser,
  APIPhoto360Request,
  APIPictureData,
  APISiteReportResponse,
  APISiteResponse,
  APISiteVideoResponse,
  APIUserResponse,
  APIVideoRequest
} from '../models'
import store from '../store'
import { ActionType, ActionType as ErrorActions } from '../store/error/actions'
import { useService, useServiceBatch } from '../utils/hook'
import { observableFromAbortableRequest } from '../utils/map'
import { buildQueryFromObject } from '../utils/parser/query'
import request, { AbortableRequest, RequestResponse } from '../utils/request'
export function useSites(
  query: Record<string, string | number | undefined>
): [
  RequestResponse<APISiteResponse[]> | undefined,
  Dispatch<SetStateAction<Record<string, string | number | undefined>>>,
  Observable<RequestResponse<APISiteResponse[]>> | undefined
] {
  return useService(
    (query) =>
      request.getRequest(buildQueryFromObject(query, 'api/locations/?'))<
        APISiteResponse[]
      >(),
    query,
    (error) => {
      if (error instanceof DOMException) return
      store.dispatch({
        type: ErrorActions.SET_BY_STATUS,
        payload: { statusCode: error.status, message: error.message }
      })
    }
  )
}
export function useSite(
  id: string | number
): [
  RequestResponse<APISiteResponse> | undefined,
  Dispatch<SetStateAction<{ id: number | string }>>,
  Observable<RequestResponse<APISiteResponse>> | undefined
] {
  return useService(
    (query) =>
      request.getRequest(`api/locations/${query.id}/`)<APISiteResponse>(),
    { id },
    (error) => {
      if (error instanceof DOMException) return
      store.dispatch({
        type: ActionType.SET_BY_STATUS,
        payload: { message: error.message, statusCode: error.status }
      })
    }
  )
}
export function useSitePhotos360<
  Query extends { site__id: number | string | undefined }
>(
  initialQuery: Query
): [
  RequestResponse<APIPhoto360Request[]> | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<RequestResponse<APIPhoto360Request[]>> | undefined
] {
  return useService(
    (query) =>
      request.getRequest(buildQueryFromObject(query, 'api/photo360/?'))(),
    initialQuery
  )
}
export function useSitePhotos<
  Query extends { site__id: number | string | undefined }
>(
  initialQuery: Query
): [
  RequestResponse<APIPictureData[]> | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<RequestResponse<APIPictureData[]>> | undefined
] {
  return useService(
    (query) =>
      request.getRequest(buildQueryFromObject(query, 'api/picture/?'))(),
    initialQuery
  )
}
export function useSiteVideos<
  Query extends { site__id: number | string | undefined }
>(
  initialQuery: Query
): [
  RequestResponse<APIVideoRequest[]> | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<RequestResponse<APIVideoRequest[]>> | undefined
] {
  return useService(
    (query) => request.getRequest(buildQueryFromObject(query, 'api/video/?'))(),
    initialQuery
  )
}
export function getSitePhotoComments(
  id: string | number | undefined
): AbortableRequest<APICommentPictureWithMakerUser[]> {
  const commentsRequest = request.getRequest(
    buildQueryFromObject(
      {
        picture__id: id
      },
      'api/commentpicture/?'
    )
  )<APICommentPicture[]>()
  const commentsWithMakerUser = commentsRequest.response.then(
    async (comments) => ({
      ...comments,
      data: await Promise.all(
        comments.data.map(async (comment) => ({
          ...comment,
          created_by:
            (comment.created_by &&
              (
                await request.getRequest(comment.created_by)<APIUserResponse>()
                  .response
              ).data) ||
            undefined
        }))
      )
    })
  )
  return {
    response: commentsWithMakerUser,
    abort: commentsRequest.abort
  }
}
export function saveSitePhotoComment(
  comment: APICommentPictureRequest
): AbortableRequest<APICommentPictureRequest> {
  return request.postRequest<
    APICommentPictureRequest,
    APICommentPictureRequest
  >('api/commentpicture/', comment, {
    'content-type': 'application/json'
  })()
}
export function useSiteDocumentation<
  Query extends { site__id: string | number | undefined }
>(
  initialQuery: Query
): [
  RequestResponse<APISiteReportResponse[]> | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<RequestResponse<APISiteReportResponse[]>> | undefined
] {
  return useService(
    (query) =>
      request.getRequest(buildQueryFromObject(query, 'api/reports/report/?'))(),
    initialQuery,
    (error) => {
      if (error instanceof DOMException) return
      store.dispatch({
        type: ActionType.SET_BY_STATUS,
        payload: { statusCode: error.statusCode, message: error.message }
      })
    }
  )
}

export function saveSiteDocument(
  formData: FormData
): Observable<RequestResponse<APISiteReportResponse>> {
  return observableFromAbortableRequest(
    request.postFormDataRequest<APISiteReportResponse>(
      '/api/reports/report/',
      formData
    )()
  )
}
export function deleteSiteDocument(
  id: number | string
): Observable<RequestResponse<APISiteReportResponse>> {
  return observableFromAbortableRequest(
    request.deleteRequest<APISiteReportResponse>(`/api/reports/report/${id}`)()
  )
}

export function useSitesInBatch<T extends { page: number }>(
  query: T
): [
  APISiteResponse[] | undefined,
  Dispatch<SetStateAction<T>>,
  Observable<APISiteResponse[]> | undefined
] {
  return useServiceBatch<APISiteResponse, T>('api/locations/?', query)
}
export function deleteVideo(id: string | number): Observable<boolean> {
  return observableFromAbortableRequest<unknown>(
    request.deleteRequest(`api/video/${id}`)()
  ).pipe(
    mapTo(true),
    catchError((error) => of(false).pipe(tap(() => console.error(error))))
  )
}
export function saveSiteVideo(
  siteURL: string,
  file: File,
  projectURL?: string
): Observable<APISiteVideoResponse> {
  const formData = new FormData()
  formData.append('video', file.name)
  formData.append('site', siteURL)
  formData.append('file', file)
  projectURL && formData.append('project', projectURL)
  return observableFromAbortableRequest(
    request.postFormDataRequest<APISiteVideoResponse>('api/video/', formData)()
  ).pipe(map((videoCreateResponse) => videoCreateResponse.data))
}
